import { useEffect } from 'react';
import { StorageUtil } from '../utils/storage-util';
import { configurationService } from '../services';
import i18n from '../utils/i18n';
import { initializeLanguageCacheManager, destroyLanguageCacheManager } from '../utils/language-cache-manager';

/**
 * Hook to initialize and manage app-wide configurations
 * Ensures all configurations are loaded from storage and properly cached
 */
export const useConfigurationInitialization = () => {
  useEffect(() => {
    // Initialize configuration service
    configurationService.getConfiguration();

    // Initialize language cache manager for automatic cache invalidation on language change
    initializeLanguageCacheManager();

    // Clean up expired cache entries on app startup
    StorageUtil.cleanupExpiredCache();

    // Migrate any legacy storage keys if needed
    migrateLegacyKeys();

    // Synchronize i18n with configuration service
    const unsubscribe = configurationService.subscribe((config) => {
      if (i18n.language !== config.language) {
        i18n.changeLanguage(config.language);
        document.documentElement.dir = config.language === 'ar' ? 'rtl' : 'ltr';
      }
    });

    // Ensure initial language is correctly set
    const currentConfig = configurationService.getConfiguration();
    if (i18n.language !== currentConfig.language) {
      i18n.changeLanguage(currentConfig.language);
      document.documentElement.dir = currentConfig.language === 'ar' ? 'rtl' : 'ltr';
    }

    return () => {
      unsubscribe();
      destroyLanguageCacheManager();
    };
  }, []);

  const migrateLegacyKeys = () => {
    // Migrate old theme key to new structure
    if (StorageUtil.hasKey('theme') && !StorageUtil.hasKey(StorageUtil.KEYS.THEME)) {
      StorageUtil.migrateKey('theme', StorageUtil.KEYS.THEME);
    }

    // Migrate old language key to new structure if not part of configuration service
    const legacyLanguage = StorageUtil.getString('i18nextLng', '');
    if (legacyLanguage && !StorageUtil.hasKey(StorageUtil.KEYS.LANGUAGE)) {
      StorageUtil.setConfig(StorageUtil.KEYS.LANGUAGE, legacyLanguage);
    }

    // Add more migrations as needed
  };

  return {
    // Return any utility functions that components might need
    clearAllCache: () => {
      StorageUtil.clear();
      configurationService.clearCache();
      window.location.reload(); // Reload to reset state
    },
    
    exportSettings: () => {
      const settings = {
        configuration: configurationService.getConfiguration(),
        theme: StorageUtil.getConfig(StorageUtil.KEYS.THEME, 'light'),
        viewMode: StorageUtil.getConfig(StorageUtil.KEYS.VIEW_MODE, 'list'),
        itemsPerPage: StorageUtil.getConfig(StorageUtil.KEYS.ITEMS_PER_PAGE, 12),
        sortPreferences: StorageUtil.getConfig(StorageUtil.KEYS.SORT_PREFERENCES, {}),
      };
      return JSON.stringify(settings, null, 2);
    },

    importSettings: (settingsJson: string) => {
      try {
        const settings = JSON.parse(settingsJson);
        
        if (settings.configuration) {
          configurationService.setConfiguration(settings.configuration);
        }
        
        if (settings.theme) {
          StorageUtil.setConfig(StorageUtil.KEYS.THEME, settings.theme);
        }
        
        if (settings.viewMode) {
          StorageUtil.setConfig(StorageUtil.KEYS.VIEW_MODE, settings.viewMode);
        }
        
        if (settings.itemsPerPage) {
          StorageUtil.setConfig(StorageUtil.KEYS.ITEMS_PER_PAGE, settings.itemsPerPage);
        }
        
        if (settings.sortPreferences) {
          StorageUtil.setConfig(StorageUtil.KEYS.SORT_PREFERENCES, settings.sortPreferences);
        }

        window.location.reload(); // Reload to apply all settings
        return true;
      } catch (error) {
        console.error('Failed to import settings:', error);
        return false;
      }
    }
  };
};